import React from 'react';
import { useDirty } from '@/hooks/useDirty';
import { confirm } from '@/stores/useConfirm';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesAlertTitle,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesIconButton,
  AgnesLabel,
  AgnesPageHeader,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTextarea,
  formatCnpj,
} from '@agnes/ui';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Info,
  Layers,
  Sparkles,
} from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { STATUS_ANALISE } from '@/constants/ConstantStatusAnaliseRubrica';
import { CLASSIFICATION_LABEL } from '@/lib/cbo';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';
import { notify } from '@/stores/useToast';
import ServiceRubricaTabela, {
  type RubricaEditRequest,
  type RubricaOcorrenciaOption,
  type RubricaTabelaItem,
  type StatusAnaliseEditavel,
} from '@/services/ServiceRubricaTabela';
import ServiceIncidenciaNatureza from '@/services/ServiceIncidenciaNatureza';
import ServiceTabelasIncidencia from '@/services/ServiceTabelasIncidencia';
import { HistoricoRubrica } from '@/pages/folha-pagamento/analise/HistoricoRubrica';
import { BadgeStatusAnalise } from '@/components/analise/BadgeStatusAnalise';
import { BadgeEmAnalise } from '@/components/analise/BadgeEmAnalise';
import { AnaliseStatus } from '@/constants/analise/AnaliseStatus';

const SITUACAO_LABEL: Record<string, string> = {
  ATIVA: 'Ativa',
  INATIVA: 'Inativa',
  EXCLUIDA: 'Excluída',
  NAO_UTILIZADA: 'Não utilizada',
};
const statusEditavel = (status: string | null): status is StatusAnaliseEditavel =>
  status === 'EM_ANALISE' || status === 'ANALISADO';
const GRAUS_VALIDOS = new Set(['CRITICO', 'ELEVADO', 'MODERADO', 'BAIXO', 'INEXPRESSIVO']);
const SEM_RECOMENDACAO = 'Em análise';
const tetoParaTexto = (teto: boolean | null) => (teto === null ? '' : teto ? 'Sim' : 'Não');
const TRUNC_SELECT =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const fmtCompetencia = (v: string | null) =>
  v && /^\d{4}-\d{2}$/.test(v) ? `${v.slice(5, 7)}/${v.slice(0, 4)}` : (v ?? '-');
const labelOcorrencia = ({ codigo, nome }: RubricaOcorrenciaOption) => `${codigo} - ${nome}`;

type CampoIncidencia = 'CP' | 'IRRF' | 'FGTS' | 'CPRP' | 'PIS';
type OpcaoIncidencia = { code: string; name: string };

const TABELA_INCIDENCIA: Record<CampoIncidencia, string> = {
  CP: 'inss',
  IRRF: 'irrf',
  FGTS: 'fgts',
  CPRP: 'cprp',
  PIS: 'pispasep',
};

const primeiroCodigo = (lista?: string) => (lista ?? '').split(',')[0]?.trim() ?? '';

const incOptions = (opcoes: OpcaoIncidencia[], atual: string): OpcaoIncidencia[] =>
  atual && !opcoes.some((o) => o.code === atual) ? [{ code: atual, name: '' }, ...opcoes] : opcoes;

type RecNatureza = {
  codigo: string;
  nome: string;
  tipo: string;
  cp: string;
  irrf: string;
  fgts: string;
  cprp: string;
  pisPasep: string;
  teto: string;
};

export function PageRubricasAnalise() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [row, setRow] = React.useState<RubricaTabelaItem | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);
    ServiceRubricaTabela.obter(Number(id))
      .then(({ data }) => {
        if (!ignore) setRow(data);
      })
      .catch(() => {
        if (!ignore) setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [id]);

  const ids: number[] = (location.state as { ids?: number[] } | null)?.ids ?? [];
  const idxAtual = ids.indexOf(Number(id));
  const anteriorId = idxAtual > 0 ? ids[idxAtual - 1] : null;
  const proximoId = idxAtual >= 0 && idxAtual < ids.length - 1 ? ids[idxAtual + 1] : null;

  const irPara = (destino: number) =>
    navigate(`/esocial/rubricas/analise/${destino}${location.search}`, {
      state: { ids },
      replace: true,
    });

  if (loading) {
    return (
      <div data-qa="rubricas-edicao-carregando" className="p-8 text-center text-agnes-text-muted">
        Carregando rubrica...
      </div>
    );
  }
  if (error || !row) {
    return (
      <div data-qa="rubricas-edicao-erro" className="p-8 text-center">
        <p className="text-agnes-text-muted">Não foi possível carregar a rubrica.</p>
        <AgnesButton
          className="mt-3"
          variant="outlined"
          onClick={() => navigate('/esocial/rubricas')}
        >
          Voltar à lista
        </AgnesButton>
      </div>
    );
  }

  return (
    <FormAnalise
      key={row.idAnaliseRubrica}
      row={row}
      onBack={() => navigate('/esocial/rubricas')}
      anteriorId={anteriorId}
      proximoId={proximoId}
      onNavegar={irPara}
    />
  );
}

// Mesma ordem de chaves do estado do formulário: o useDirty compara por JSON.stringify.
const valoresDaRubrica = (r: RubricaTabelaItem) => ({
  tipo: r.tipoCodigo ?? '',
  natureza: String(r.naturezaCodigo ?? ''),
  ocorrencia: r.ocorrencia ? String(r.ocorrencia.codigo) : '',
  statusAnalise: r.statusAnalise ?? '',
  inc: {
    CP: r.cp ?? '',
    IRRF: r.irrf ?? '',
    FGTS: r.fgts ?? '',
    CPRP: r.cprp ?? '',
    PIS: r.pis ?? '',
    TETO: tetoParaTexto(r.teto),
  },
  coment: '',
});

function FormAnalise({
  row,
  onBack,
  anteriorId,
  proximoId,
  onNavegar,
}: {
  row: RubricaTabelaItem;
  onBack: () => void;
  anteriorId: number | null;
  proximoId: number | null;
  onNavegar: (id: number) => void;
}) {
  const [rubrica, setRubrica] = React.useState(row);
  const inicial = valoresDaRubrica(row);
  const [tipo, setTipo] = React.useState(inicial.tipo);
  const [natureza, setNatureza] = React.useState(inicial.natureza);
  const [ocorrencia, setOcorrencia] = React.useState(inicial.ocorrencia);
  const [statusAnalise, setStatusAnalise] = React.useState(inicial.statusAnalise);
  const [inc, setInc] = React.useState(inicial.inc);
  const [coment, setComent] = React.useState(inicial.coment);
  const [salvando, setSalvando] = React.useState(false);
  const [opcoesOcorrencia, setOpcoesOcorrencia] = React.useState<RubricaOcorrenciaOption[]>([]);
  const [carregandoOcorrencias, setCarregandoOcorrencias] = React.useState(true);
  const [erroOcorrencias, setErroOcorrencias] = React.useState(false);
  const setI = (k: keyof typeof inc) => (v: string) => setInc((s) => ({ ...s, [k]: v }));

  // Aba ativa (Informações | Histórico). Deep-link via ?tab=historico (ícone da listagem).
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = React.useState(
    searchParams.get('tab') === 'historico' ? 'historico' : 'informacoes',
  );
  const mudarTab = (v: string) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'historico') next.set('tab', 'historico');
    else next.delete('tab');
    setSearchParams(next, { replace: true, state: location.state });
  };

  React.useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    ServiceRubricaTabela.consultarOcorrencias(controller.signal)
      .then(({ data }) => {
        if (!ignore) setOpcoesOcorrencia(data);
      })
      .catch(() => {
        if (!ignore) setErroOcorrencias(true);
      })
      .finally(() => {
        if (!ignore) setCarregandoOcorrencias(false);
      });
    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  const ocorrenciaAtualIndisponivel =
    rubrica.ocorrencia &&
    !opcoesOcorrencia.some((opcao) => opcao.codigo === rubrica.ocorrencia?.codigo);
  const codigoOcorrenciaSelecionada = ocorrencia ? Number(ocorrencia) : null;
  const ocorrenciaAlterada = codigoOcorrenciaSelecionada !== (rubrica.ocorrencia?.codigo ?? null);
  const ocorrenciaSelecionada = opcoesOcorrencia.find(
    (opcao) => opcao.codigo === codigoOcorrenciaSelecionada,
  );
  const diagnosticoVisivel =
    ocorrenciaAlterada && ocorrenciaSelecionada
      ? (ocorrenciaSelecionada.diagnostico ?? '—')
      : (rubrica.diagnostico ?? '—');
  const classificacao =
    ocorrenciaAlterada && ocorrenciaSelecionada
      ? ocorrenciaSelecionada.classificacao
      : rubrica.classificacao;
  const classificacaoVisivel = classificacao
    ? (CLASSIFICATION_LABEL[classificacao] ?? classificacao)
    : '—';
  const grauRiscoVisivel =
    ocorrenciaAlterada && ocorrenciaSelecionada
      ? ocorrenciaSelecionada.grauRisco
      : rubrica.grauRisco;

  const [naturezas, setNaturezas] = React.useState<RecNatureza[]>([]);
  React.useEffect(() => {
    ServiceIncidenciaNatureza.search({}, { size: 200 })
      .then(({ data }) => setNaturezas(data.content ?? []))
      .catch(() => setNaturezas([]));
  }, []);

  const [opcoesInc, setOpcoesInc] = React.useState<Record<CampoIncidencia, OpcaoIncidencia[]>>({
    CP: [],
    IRRF: [],
    FGTS: [],
    CPRP: [],
    PIS: [],
  });
  React.useEffect(() => {
    const campos = Object.keys(TABELA_INCIDENCIA) as CampoIncidencia[];
    Promise.all(
      campos.map((campo) =>
        ServiceTabelasIncidencia.search(TABELA_INCIDENCIA[campo], {}, { size: 200, sort: 'code' })
          .then(({ data }) => (data.content ?? []) as OpcaoIncidencia[])
          .catch(() => [] as OpcaoIncidencia[]),
      ),
    ).then((listas) =>
      setOpcoesInc(
        Object.fromEntries(campos.map((c, i) => [c, listas[i]])) as Record<
          CampoIncidencia,
          OpcaoIncidencia[]
        >,
      ),
    );
  }, []);

  const aplicarRecomendacao = (codigo: string) => {
    const rec = naturezas.find((n) => n.codigo === codigo);
    if (!rec) return;
    setInc({
      CP: primeiroCodigo(rec.cp),
      IRRF: primeiroCodigo(rec.irrf),
      FGTS: primeiroCodigo(rec.fgts),
      CPRP: primeiroCodigo(rec.cprp),
      PIS: primeiroCodigo(rec.pisPasep),
      TETO: rec.teto,
    });
    setTipo(primeiroCodigo(rec.tipo));
  };

  const valoresFormulario = { tipo, natureza, ocorrencia, statusAnalise, inc, coment };
  const { isDirty, rebase } = useDirty(valoresFormulario);
  const confirmarDescarte = async () =>
    !isDirty ||
    (await confirm({
      title: 'Alterações não salvas',
      message: 'Você tem alterações que não foram salvas e serão perdidas. Deseja sair?',
      okLabel: 'Sair',
      cancelLabel: 'Continuar editando',
    }));

  const sairComAviso = async () => {
    if (await confirmarDescarte()) onBack();
  };

  const navegarPara = async (destino: number | null) => {
    if (destino === null || salvando) return;
    if (await confirmarDescarte()) onNavegar(destino);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const body: RubricaEditRequest = {
        tipo,
        natureza: natureza ? Number(natureza) : null,
        cp: inc.CP,
        irrf: inc.IRRF,
        fgts: inc.FGTS,
        cprp: inc.CPRP,
        pis: inc.PIS,
        teto: inc.TETO ? inc.TETO === 'Sim' : null,
      };
      if (ocorrenciaAlterada && codigoOcorrenciaSelecionada !== null) {
        body.ocorrencia = codigoOcorrenciaSelecionada;
      }
      if (statusEditavel(statusAnalise) && statusAnalise !== rubrica.statusAnalise) {
        body.statusAnalise = statusAnalise;
      }
      const { data: atualizada } = await ServiceRubricaTabela.editar(
        rubrica.idAnaliseRubrica,
        body,
      );
      if (ocorrenciaAlterada && atualizada) {
        const valoresAtualizados = valoresDaRubrica(atualizada);
        setRubrica(atualizada);
        setTipo(valoresAtualizados.tipo);
        setNatureza(valoresAtualizados.natureza);
        setOcorrencia(valoresAtualizados.ocorrencia);
        setStatusAnalise(valoresAtualizados.statusAnalise);
        setInc(valoresAtualizados.inc);
        setComent(valoresAtualizados.coment);
        rebase(valoresAtualizados);
        notify.success('Ocorrência atualizada; recomendações recalculadas.');
        return;
      }
      notify.success('Rubrica atualizada com sucesso.');
      onBack();
    } catch {
      notify.error('Não foi possível salvar a rubrica. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const grauValido = grauRiscoVisivel && GRAUS_VALIDOS.has(grauRiscoVisivel);
  const grauSalvoValido = rubrica.grauRisco && GRAUS_VALIDOS.has(rubrica.grauRisco);

  return (
    <div data-qa="rubricas-edicao">
      <AgnesPageHeader
        icon={<ClipboardList />}
        title="Análise da rubrica"
        subtitle="Compare os dados do cliente com as recomendações Agnes"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Rubricas' },
          { label: 'Análise da rubrica' },
        ]}
      />

      <AgnesTabs className="mt-2 w-full" value={tab} onValueChange={mudarTab}>
        <AgnesTabsList>
          <AgnesTabsTrigger value="informacoes" data-qa="rubricas-edicao-aba-informacoes">
            Informações
          </AgnesTabsTrigger>
          <AgnesTabsTrigger value="historico" data-qa="rubricas-edicao-aba-historico">
            Histórico de recomendações
          </AgnesTabsTrigger>
        </AgnesTabsList>

        <AgnesTabsContent value="informacoes">
          <AgnesCard>
            <AgnesCardContent className="flex items-stretch">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-agnes-border pb-3">
                <div>
                  <p className="text-lg font-bold text-agnes-primary">
                    {rubrica.codigoRubrica} - {rubrica.descricao}
                  </p>
                  <p className="text-sm text-agnes-text-muted">Detalhes da rubrica selecionada</p>
                </div>

                <div className="flex flex-row items-center justify-end gap-6">
                  <div className="flex flex-col items-start gap-1">
                    <AgnesLabel>Status de Análise</AgnesLabel>
                    {rubrica.statusAnalise ? (
                      <BadgeStatusAnalise status={rubrica.statusAnalise as AnaliseStatus} />
                    ) : (
                      <BadgeEmAnalise />
                    )}
                  </div>

                  <div className="flex flex-col items-start gap-1">
                    <AgnesLabel>Grau de Risco</AgnesLabel>
                    {grauSalvoValido ? (
                      <BadgeGrauExposicaoRisco
                        grau={rubrica.grauRisco as AnaliseGrauExposicaoRisco}
                      />
                    ) : (
                      <BadgeEmAnalise />
                    )}
                  </div>

                  <div className="flex flex-row items-center gap-2">
                    <AgnesIconButton
                      aria-label="Anterior"
                      size="sm"
                      disabled={anteriorId === null || salvando}
                      onClick={() => navegarPara(anteriorId)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </AgnesIconButton>
                    <AgnesIconButton
                      aria-label="Próxima"
                      size="sm"
                      disabled={proximoId === null || salvando}
                      onClick={() => navegarPara(proximoId)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </AgnesIconButton>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-4 rounded-xl border border-agnes-border p-4 text-sm md:grid-cols-4">
                {[
                  [
                    'CNPJ',
                    rubrica.cnpjEmpregador ? formatCnpj(rubrica.cnpjEmpregador) : '-',
                    'cnpj',
                  ],
                  ['Id. da tabela', rubrica.idTabela ?? '-', 'id-tabela'],
                  ['Código', rubrica.codigoRubrica ?? '-', 'codigo'],
                  ['Tipo', rubrica.tipoNome ?? '-', 'tipo'],
                  ['Início da validade', fmtCompetencia(rubrica.inicioValidade), 'inicio-validade'],
                  ['Recepção no eSocial', '-', 'recepcao'],
                  ['Última utilização', '-', 'ultima-utilizacao'],
                  [
                    'Situação',
                    rubrica.situacao ? (SITUACAO_LABEL[rubrica.situacao] ?? rubrica.situacao) : '-',
                    'situacao',
                  ],
                ].map(([k, v, qa]) => (
                  <div key={k}>
                    <p className="text-agnes-text-muted">{k}</p>
                    <p
                      data-qa={`rubricas-edicao-detalhe-${qa}`}
                      className="font-semibold text-agnes-text"
                    >
                      {v}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-agnes-border p-4">
                <p className="mb-3 flex items-center gap-2 font-semibold text-agnes-primary">
                  <FileText className="h-4 w-4" /> Descrição, tipo e natureza
                </p>

                <AgnesLabel>Descrição (agrupada)</AgnesLabel>
                <div className="mb-3 w-full min-h-[46px] px-3 py-2 flex items-center rounded-lg bg-agnes-bg-muted text-sm">
                  {rubrica.descricao ?? '-'}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <AgnesLabel>Tipo</AgnesLabel>
                    <AgnesSelect value={tipo} onValueChange={setTipo}>
                      <AgnesSelectTrigger data-qa="rubricas-edicao-tipo" className={TRUNC_SELECT}>
                        <AgnesSelectValue placeholder={SEM_RECOMENDACAO} />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent>
                        {[
                          { value: '1', label: '1 - Vencimento, provento ou pensão' },
                          { value: '2', label: '2 - Desconto' },
                          { value: '3', label: '3 - Informativa' },
                          { value: '4', label: '4 - Informativa dedutora' },
                        ].map((option) => (
                          <AgnesSelectItem key={option.value} value={option.value}>
                            {option.label}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </div>

                  <div>
                    <AgnesLabel>Natureza</AgnesLabel>
                    <AgnesSelect
                      value={natureza}
                      onValueChange={(v) => {
                        setNatureza(v);
                        aplicarRecomendacao(v); // troca de natureza -> recarrega incidências recomendadas
                      }}
                    >
                      <AgnesSelectTrigger
                        data-qa="rubricas-edicao-natureza"
                        className={TRUNC_SELECT}
                      >
                        <AgnesSelectValue placeholder={SEM_RECOMENDACAO} />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent>
                        {naturezas.map((n) => (
                          <AgnesSelectItem key={n.codigo} value={n.codigo}>
                            {n.codigo} - {n.nome}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </div>
                </div>

                <p className="mt-2 flex items-center gap-1.5 text-sm text-agnes-success">
                  <Sparkles className="h-3.5 w-3.5" /> Ao trocar a natureza, as incidências são
                  recarregadas automaticamente
                </p>
              </div>

              <div className="mt-4 rounded-xl border border-agnes-border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="flex items-center gap-2 font-semibold text-agnes-primary">
                    <Layers className="h-4 w-4" /> Incidências
                  </p>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      data-qa="rubricas-edicao-restaurar"
                      onClick={() => aplicarRecomendacao(natureza)}
                      className="text-sm text-agnes-primary hover:underline"
                    >
                      Restaurar recomendação
                    </button>
                    <span className="rounded-full bg-agnes-success/15 px-2 py-0.5 text-xs font-medium text-agnes-success">
                      Auto-preenchido pela natureza
                    </span>
                  </div>
                </div>

                <AgnesAlert
                  icon={<Sparkles className="h-4 w-4 text-agnes-primary" />}
                  className="mb-3"
                >
                  <AgnesAlertTitle>Preenchimento automático</AgnesAlertTitle>
                  <AgnesAlertContent>
                    Códigos de incidência <b>preenchidos automaticamente</b> pela natureza (tabela
                    eSocial v1.3). Podem ser <b>editados</b> manualmente.
                  </AgnesAlertContent>
                </AgnesAlert>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                  {(['CP', 'IRRF', 'FGTS', 'CPRP', 'PIS'] as const).map((k) => (
                    <div key={k}>
                      <AgnesLabel>COD INC {k}</AgnesLabel>
                      <AgnesSelect value={inc[k]} onValueChange={setI(k)}>
                        <AgnesSelectTrigger
                          data-qa={`rubricas-edicao-inc-${k.toLowerCase()}`}
                          className={`w-full min-w-0 ${TRUNC_SELECT}`}
                        >
                          <AgnesSelectValue placeholder={SEM_RECOMENDACAO} />
                        </AgnesSelectTrigger>
                        <AgnesSelectContent
                          position="popper"
                          className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
                        >
                          {incOptions(opcoesInc[k], inc[k]).map((o) => {
                            const label = o.name ? `${o.code} - ${o.name}` : o.code;
                            return (
                              <AgnesSelectItem
                                key={o.code}
                                value={o.code}
                                data-qa={`rubricas-edicao-inc-${k.toLowerCase()}-opcao-${o.code}`}
                                className="[&>span:last-child]:min-w-0"
                              >
                                <span className="block w-full min-w-0 truncate" title={label}>
                                  {label}
                                </span>
                              </AgnesSelectItem>
                            );
                          })}
                        </AgnesSelectContent>
                      </AgnesSelect>
                    </div>
                  ))}

                  <div>
                    <AgnesLabel>TETO REMUNERATÓRIO</AgnesLabel>
                    <AgnesSelect value={inc.TETO} onValueChange={setI('TETO')}>
                      <AgnesSelectTrigger data-qa="rubricas-edicao-teto" className={TRUNC_SELECT}>
                        <AgnesSelectValue placeholder={SEM_RECOMENDACAO} />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent>
                        {['Sim', 'Não'].map((value) => (
                          <AgnesSelectItem key={value} value={value}>
                            {value}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-agnes-border p-4">
                <p className="mb-3 flex items-center gap-2 font-semibold text-agnes-primary">
                  <Sparkles className="h-4 w-4" /> Análise Agnes
                </p>
                <AgnesLabel htmlFor="rubricas-edicao-ocorrencia">Ocorrência</AgnesLabel>
                <AgnesSelect value={ocorrencia} onValueChange={setOcorrencia}>
                  <AgnesSelectTrigger
                    id="rubricas-edicao-ocorrencia"
                    data-qa="rubricas-edicao-ocorrencia"
                    className={`mb-3 w-full ${TRUNC_SELECT}`}
                    disabled={carregandoOcorrencias || erroOcorrencias || !opcoesOcorrencia.length}
                  >
                    <AgnesSelectValue
                      placeholder={
                        carregandoOcorrencias
                          ? 'Carregando ocorrências...'
                          : erroOcorrencias
                            ? 'Opções indisponíveis'
                            : 'Selecione uma ocorrência'
                      }
                    />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent
                    position="popper"
                    className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
                    data-qa="rubricas-edicao-ocorrencia-popover"
                  >
                    {ocorrenciaAtualIndisponivel && (
                      <AgnesSelectItem
                        key={`indisponivel-${rubrica.ocorrencia?.codigo}`}
                        value={String(rubrica.ocorrencia?.codigo)}
                        disabled
                        data-qa={`rubricas-edicao-ocorrencia-indisponivel-${rubrica.ocorrencia?.codigo}`}
                        className="[&>span:last-child]:min-w-0"
                      >
                        <span
                          className="block w-full min-w-0 truncate"
                          title={`${rubrica.ocorrencia?.codigo} - ${rubrica.ocorrencia?.nome ?? 'Ocorrência atual'} (indisponível)`}
                        >
                          {rubrica.ocorrencia?.codigo} -{' '}
                          {rubrica.ocorrencia?.nome ?? 'Ocorrência atual'}
                          {' (indisponível)'}
                        </span>
                      </AgnesSelectItem>
                    )}
                    {opcoesOcorrencia.map((opcao) => (
                      <AgnesSelectItem
                        key={opcao.codigo}
                        value={String(opcao.codigo)}
                        data-qa={`rubricas-edicao-ocorrencia-opcao-${opcao.codigo}`}
                        className="[&>span:last-child]:min-w-0"
                      >
                        <span
                          className="block w-full min-w-0 truncate"
                          title={labelOcorrencia(opcao)}
                        >
                          {labelOcorrencia(opcao)}
                        </span>
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
                {erroOcorrencias && (
                  <p role="alert" className="mb-3 text-sm text-agnes-error">
                    Não foi possível carregar as opções de ocorrência.
                  </p>
                )}
                <AgnesLabel>Diagnóstico</AgnesLabel>
                <div
                  data-qa="rubricas-edicao-diagnostico"
                  className="mb-3 w-full min-h-[46px] px-3 py-2 flex items-center rounded-lg bg-agnes-bg-muted text-sm text-agnes-text"
                >
                  {diagnosticoVisivel}
                </div>
                <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <AgnesLabel htmlFor="rubricas-edicao-analise-status">
                      Status de Análise
                    </AgnesLabel>
                    <AgnesSelect value={statusAnalise} onValueChange={setStatusAnalise}>
                      <AgnesSelectTrigger
                        id="rubricas-edicao-analise-status"
                        data-qa="rubricas-edicao-analise-status"
                        className={TRUNC_SELECT}
                      >
                        <AgnesSelectValue placeholder="Selecione o status" />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent selectionType="check">
                        {STATUS_ANALISE.filter(
                          ({ value }) => statusEditavel(value) || value === rubrica.statusAnalise,
                        ).map(({ value }) => (
                          <AgnesSelectItem
                            key={value}
                            value={value}
                            disabled={!statusEditavel(value)}
                            data-qa={`rubricas-edicao-analise-status-opcao-${value}`}
                          >
                            <BadgeStatusAnalise status={value} />
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </div>
                  <div>
                    <AgnesLabel>Classificação</AgnesLabel>
                    <div
                      data-qa="rubricas-edicao-classificacao"
                      className="w-full min-h-[46px] px-3 py-2 flex items-center rounded-lg bg-agnes-bg-muted text-sm text-agnes-text"
                    >
                      {classificacaoVisivel}
                    </div>
                  </div>
                  <div>
                    <AgnesLabel>Grau de Risco</AgnesLabel>
                    <div className="w-full min-h-[46px] px-3 py-2 flex items-center rounded-lg bg-agnes-bg-muted text-sm text-agnes-text">
                      {grauValido ? (
                        <BadgeGrauExposicaoRisco
                          grau={grauRiscoVisivel as AnaliseGrauExposicaoRisco}
                        />
                      ) : (
                        <span className="text-sm text-agnes-text-muted">Em análise</span>
                      )}
                    </div>
                  </div>
                </div>
                <AgnesLabel>Comentário da análise</AgnesLabel>
                <AgnesTextarea
                  data-qa="rubricas-edicao-comentario"
                  value={coment}
                  onChange={(e) => setComent(e.target.value)}
                  placeholder="Registre observações, justificativas ou orientações sobre esta rubrica…"
                  rows={3}
                />
                <p className="mt-1 flex items-center gap-1.5 text-sm text-agnes-text-muted">
                  <Info className="h-3.5 w-3.5" /> O comentário é anexado ao histórico de
                  movimentações da rubrica.
                </p>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-agnes-border pt-3">
                <AgnesButton
                  data-qa="rubricas-edicao-voltar"
                  variant="text"
                  startIcon={<ChevronLeft className="h-4 w-4" />}
                  onClick={sairComAviso}
                >
                  Voltar à lista
                </AgnesButton>
                <div className="flex gap-3">
                  <AgnesButton
                    data-qa="rubricas-edicao-cancelar"
                    variant="outlined"
                    onClick={sairComAviso}
                  >
                    Cancelar
                  </AgnesButton>
                  <AgnesButton
                    data-qa="rubricas-tabela-edicao-salvar"
                    variant="primary"
                    startIcon={<Check className="h-4 w-4" />}
                    onClick={salvar}
                    disabled={!isDirty || salvando}
                  >
                    {salvando ? 'Salvando...' : 'Salvar'}
                  </AgnesButton>
                </div>
              </div>
            </AgnesCardContent>
          </AgnesCard>
        </AgnesTabsContent>

        <AgnesTabsContent value="historico">
          <AgnesCard>
            <AgnesCardContent>
              <HistoricoRubrica rubrica={rubrica} />
            </AgnesCardContent>
          </AgnesCard>
        </AgnesTabsContent>
      </AgnesTabs>
    </div>
  );
}
